import { useMemo } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    getCoreRowModel,
    getSortedRowModel,
    OnChangeFn,
    RowSelectionState,
    useReactTable,
    getPaginationRowModel,
    getFilteredRowModel,
    Column,
    getFacetedUniqueValues,
} from "@tanstack/react-table";
import { Checkbox, Select } from "antd";
import { Control, useWatch } from "react-hook-form";

import {
    ControlWithoutFieldsGraphItemDto,
    ControlWithoutFieldsGraphItemDtoListDto,
    ControlWithoutFieldsResources,
} from "api/generated/model";
import { TagsSelectFilter } from "components/table/tagsFilter/tagsSelectFilter";
import { TagList } from "components/tag/tagList";
import { TagsData } from "components/tag/types";
import { useControlsSuspenseQuery } from "org/control/queries/useControlsQuery";
import { useTagsSuspenseQuery } from "org/tags/queries/useTagsQuery";
import { formatTagsAndCategoriesString } from "org/tags/tagsTSTUtils";

import { useUserGroupsQuery } from "../../../../user/queries/useUserGroupsQuery";
import { TestingControlFormSchema } from "../testingControlSchema";
import { getAllTagsFromControl } from "../utils";

import {
    bulkControlsTableColumnHelper,
    getBulkControlsProgramColumn,
    getBulkControlsSchedulingColumn,
    getBulkControlsTitleColumn,
} from "./bulkUtils";
import {
    BULK_CONTROLS_CONTROL_PROGRAM_COLUMN_ID,
    BULK_CONTROLS_SCHEDULING_INTERVAL_COLUMN_ID,
    BULK_CONTROLS_TITLE_COLUMN_ID,
} from "./consts";

export function useSelectControlsTable(
    rowSelection: RowSelectionState,
    onRowSelectionChange: OnChangeFn<RowSelectionState>,
    selectedControlIdsFieldValue: string[] | undefined,
    control: Control<TestingControlFormSchema>
) {
    const { data: tagsData } = useTagsSuspenseQuery();
    const { t } = useTranslation();
    const { data } = useControlsSuspenseQuery();
    const samplingTags = useWatch({ control, name: "samplingTagIds" });
    const { data: userGroupTagData } = useUserGroupsQuery();

    const tableData = useMemo(
        () =>
            data.content
                .map((listDto) => listDto.resource)
                // Filter selected
                .filter(
                    (control) =>
                        !selectedControlIdsFieldValue?.includes(control.id)
                )
                // Filter to match sampling tags
                .filter((control) =>
                    getAllTagsFromControl(control, userGroupTagData ?? []).some(
                        (tagId) =>
                            samplingTags.some(
                                (samplingTag) => samplingTag === tagId
                            )
                    )
                ),
        [data, selectedControlIdsFieldValue, samplingTags, userGroupTagData]
    );

    const table = useReactTable({
        columns: buildColumns(t, data.linkedResources, tagsData),
        data: tableData,
        initialState: {
            sorting: [
                {
                    id: BULK_CONTROLS_TITLE_COLUMN_ID,
                    desc: false,
                },
            ],
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
            },
        },
        state: {
            rowSelection,
        },
        onRowSelectionChange,
        getRowId: (row) => row.id,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    return {
        table,
        t,
        data,
    };
}

const BULK_CONTROLS_TAGS_COLUMN_ID = "tags";

function buildColumns(
    t: TFunction,
    resources: ControlWithoutFieldsResources,
    tagsData: TagsData
) {
    return [
        bulkControlsTableColumnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            cell: (info) => {
                const onClick = () => {
                    info.row.toggleSelected();
                };
                return (
                    <Checkbox
                        checked={info.row.getIsSelected()}
                        onClick={onClick}
                    />
                );
            },
            header: (info) => {
                return (
                    <Checkbox
                        checked={info.table.getIsAllPageRowsSelected()}
                        onChange={info.table.getToggleAllRowsSelectedHandler()}
                    />
                );
            },
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
        }),
        getBulkControlsTitleColumn(t),
        getBulkControlsProgramColumn(t, resources),
        getBulkControlsSchedulingColumn(t),
        bulkControlsTableColumnHelper.accessor(
            (row) => formatTagsAndCategoriesString(row.tagIds, tagsData),
            {
                id: BULK_CONTROLS_TAGS_COLUMN_ID,
                header: t("TestingControlView_BulkAdd_TagsColumn"),
                filterFn: (row, _id, filterValue: string[]) => {
                    if (!filterValue || filterValue.length === 0) {
                        return true;
                    }
                    return row.original.tagIds.some((tagId) =>
                        filterValue.includes(tagId)
                    );
                },
                cell: (info) => (
                    <TagList
                        tags={tagsData.tags.filter((tag) =>
                            info.row.original.tagIds.includes(tag.id)
                        )}
                        mode="HOVER"
                        tagCategories={tagsData.tagCategories}
                    />
                ),
            }
        ),
    ];
}

type SelectControlsTableAccessorColumnId =
    | typeof BULK_CONTROLS_TITLE_COLUMN_ID
    | typeof BULK_CONTROLS_CONTROL_PROGRAM_COLUMN_ID
    | typeof BULK_CONTROLS_SCHEDULING_INTERVAL_COLUMN_ID
    | typeof BULK_CONTROLS_TAGS_COLUMN_ID;

export function getFilterComponent(
    t: TFunction,
    column: Column<ControlWithoutFieldsGraphItemDto>,
    controlsData: ControlWithoutFieldsGraphItemDtoListDto
) {
    const columnId = column.id as SelectControlsTableAccessorColumnId;
    switch (columnId) {
        case "controlProgram": {
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const labels = [...values.keys()];
            const options = labels.map((program) => ({
                label: program,
                value: program,
            }));

            return (
                <Select
                    options={options}
                    autoFocus
                    mode="multiple"
                    placeholder={t(
                        "TestingControlView_Bulk_ControlProgramFilterPlaceHolder"
                    )}
                    dropdownMatchSelectWidth={false}
                    style={{ minWidth: 200 }}
                    value={column.getFilterValue() ?? undefined}
                    onChange={(value) => {
                        column.setFilterValue(value);
                    }}
                />
            );
        }
        case "schedulingInterval": {
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const labels = [...values.keys()];
            const options = labels.map((interval) => ({
                label: interval,
                value: interval,
            }));

            return (
                <Select
                    options={options}
                    autoFocus
                    mode="multiple"
                    placeholder={t(
                        "TestingControlView_Bulk_SchedulingIntervalFilterPlaceHolder"
                    )}
                    dropdownMatchSelectWidth={false}
                    style={{ minWidth: 200 }}
                    value={column.getFilterValue() ?? undefined}
                    onChange={(value) => {
                        column.setFilterValue(value);
                    }}
                />
            );
        }
        case "tags": {
            const rows = column.getFacetedRowModel();
            const tagIdsPerRow = rows.flatRows.map(
                (row) => row.original.tagIds
            );
            const uniqueTagIds = Array.from(new Set(tagIdsPerRow.flat()));
            return (
                <TagsSelectFilter
                    tagCategories={controlsData.linkedResources.tagCategories}
                    tags={controlsData.linkedResources.tags.map(
                        (tag) => tag.resource
                    )}
                    uniqueTagIds={uniqueTagIds}
                    placeholder={t(
                        "TestingControlView_Bulk_TagFilterPlaceholder"
                    )}
                    setFilterValue={column.setFilterValue}
                    value={column.getFilterValue() as string[]}
                />
            );
        }
        case "title": {
            throw new Error("Not filterable");
        }

        default:
            return assertNever(columnId);
    }
}
